import React, { useState } from 'react';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import StbButton from '../security-toolbox-ui/StbButton';
import { asSingleValue } from '../../support/utils';
import { useLabInjections, useLabScenarios, useReapplyLabInjection } from '../../hooks/lab-hooks';
import {
  LabInjection,
  labInsertModeLabel,
  labInjectionSentiment,
  LAB_INJECTION_VERDICT_LABELS,
} from '../../support/labService';
import {
  LabCard,
  LabDate,
  LabMessage,
  LabPanelState,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';

export type LabInjectionsPanelProps = {
  /** Re-applying is a steward action. Reading the history is not. */
  canManage?: boolean;
};

const stateSentiment = (state: LabInjection['state']) => {
  if (state === 'completed') return 'success' as const;
  if (state === 'failed') return 'danger' as const;
  if (state === 'partial') return 'warning' as const;
  return 'info' as const;
};

/**
 * What has been pushed at running pods, and where it landed.
 *
 * The per-run verdicts matter more than the injection's own state. `skipped_past` is a
 * catch-up step correctly declining to disturb somebody who is already beyond it - a success,
 * not a miss - and reading it as a failure is how somebody ends up re-pushing a password reset
 * at people halfway through the next exercise.
 *
 * Re-apply is a full re-sweep, not a retry of the runs that errored. It is how a pod that
 * started after the first push gets the step; runs that already have it come back
 * `already_present`.
 */
const LabInjectionsPanel: React.FC<LabInjectionsPanelProps> = ({ canManage = false }) => {
  const [scenarioSlug, setScenarioSlug] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);

  const { data: scenarios } = useLabScenarios();
  const { data: injections, isLoading, error, refetch } = useLabInjections(
    scenarioSlug ? { scenario: scenarioSlug } : {},
  );
  const reapply = useReapplyLabInjection();

  const handleReapply = async (injection: LabInjection) => {
    if (
      // eslint-disable-next-line no-restricted-globals
      !window.confirm(
        `Re-sweep "${injection.step_name}"? Every open run of ${injection.scenario_slug} is evaluated again, ` +
          'including pods that started since the first push. This is not a retry of the runs that errored.',
      )
    ) {
      return;
    }

    setActionError(null);
    setActionNote(null);

    try {
      const result = await reapply.mutateAsync(injection.id);
      setActionNote(
        `Re-swept ${result.step_key}: ${result.runs_applied} applied, ${result.runs_skipped} skipped, ${result.runs_error} errored across ${result.runs_seen} run${result.runs_seen === 1 ? '' : 's'}.`,
      );
      setExpanded(result.id);
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not re-sweep the injection.'));
    }
  };

  return (
    <LabCard
      title="Mid-session pushes"
      subtitle="Steps sent at pods that were already running."
      actions={
        <>
          <div className="min-w-[220px]">
            <HbrSelect
              value={scenarioSlug}
              size="small"
              onHbr-change={(evt: any) => setScenarioSlug(asSingleValue(evt.target.value))}
            >
              <HbrOption value="">All scenarios</HbrOption>
              {(scenarios || []).map((scenario) => (
                <HbrOption value={scenario.slug} key={scenario.slug}>
                  {scenario.name}
                </HbrOption>
              ))}
              <div slot="label">Scenario</div>
            </HbrSelect>
          </div>
          <StbButton role="default" size="small" onClick={async () => refetch()}>
            Refresh
          </StbButton>
        </>
      }
    >
      {actionNote && <LabMessage>{actionNote}</LabMessage>}
      {actionError && <LabMessage tone="warning">{actionError}</LabMessage>}

      <LabPanelState
        loading={isLoading}
        error={error}
        empty={!injections?.length}
        emptyMessage="Nothing has been pushed mid-session. Steps go out from a scenario's step list."
        loadingLabel="Loading pushes..."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 font-semibold">Step</th>
                <th className="px-3 py-2 font-semibold">Mode</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold">Runs</th>
                <th className="px-3 py-2 font-semibold">Pushed</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {(injections || []).map((injection) => (
                <React.Fragment key={injection.id}>
                  <tr className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">{injection.step_name}</div>
                      <div className="font-mono text-xs text-gray-500">{injection.step_key}</div>
                      <div className="text-xs text-gray-500">
                        {injection.scenario_slug}
                        {injection.pool_slug ? ` • pool ${injection.pool_slug}` : ' • every open run'}
                      </div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-xs">{labInsertModeLabel(injection.insert_mode)}</div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      <LabStatusTag
                        label={injection.state}
                        sentiment={stateSentiment(injection.state)}
                      />
                      {injection.error_message && (
                        <div className="text-xs text-red-700">{injection.error_message}</div>
                      )}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <div>{`${injection.runs_seen} reached`}</div>
                      <div className="text-green-700">{`${injection.runs_applied} applied`}</div>
                      {injection.runs_skipped > 0 && (
                        <div className="text-gray-600">
                          {`${injection.runs_skipped} skipped on purpose`}
                        </div>
                      )}
                      {injection.runs_error > 0 && (
                        <div className="text-red-700">{`${injection.runs_error} errored`}</div>
                      )}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                      <LabDate value={injection.started_at} />
                      <div>{injection.initiated_by_email}</div>
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <StbButton
                          role="default"
                          size="xsmall"
                          onClick={async () =>
                            setExpanded(expanded === injection.id ? null : injection.id)
                          }
                        >
                          {expanded === injection.id ? 'Hide' : 'Per pod'}
                        </StbButton>

                        {canManage && (
                          <StbButton
                            role="update"
                            size="xsmall"
                            loading={reapply.isPending}
                            onClick={async () => handleReapply(injection)}
                          >
                            Re-sweep
                          </StbButton>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expanded === injection.id && (
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <td className="px-3 py-3" colSpan={6}>
                        {injection.reason && (
                          <p className="m-0 mb-2 text-xs text-gray-600">
                            <span className="uppercase text-gray-500">Reason </span>
                            {injection.reason}
                          </p>
                        )}

                        {(injection.results || []).length ? (
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-gray-200 uppercase tracking-wide text-gray-500">
                                <th className="px-2 py-1 font-semibold">Pod</th>
                                <th className="px-2 py-1 font-semibold">Verdict</th>
                                <th className="px-2 py-1 font-semibold">Detail</th>
                              </tr>
                            </thead>

                            <tbody>
                              {(injection.results || []).map((result) => (
                                <tr key={result.id} className="border-b border-gray-100 align-top">
                                  <td className="px-2 py-1 font-mono">{result.session_key}</td>

                                  <td className="px-2 py-1 whitespace-nowrap">
                                    <LabStatusTag
                                      label={
                                        LAB_INJECTION_VERDICT_LABELS[result.verdict] ||
                                        result.verdict
                                      }
                                      sentiment={labInjectionSentiment(result.verdict)}
                                    />
                                  </td>

                                  <td className="px-2 py-1">{result.detail || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="m-0 text-xs text-gray-600">
                            No runs were reached. Either nothing was open, or the pool filter
                            matched no live pods.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </LabPanelState>
    </LabCard>
  );
};

export default LabInjectionsPanel;
